import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@mui/material/locale";
import { ThemeProvider } from "@mui/material/styles";
import { createShessaiTheme } from "./theme/shessaiTheme";
import { useMediaQuery } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";

import "./styles/animations.css";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    localStorage.getItem("primaryColorDark") ||
    "#0399FF";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  // Toggle body class for Liquid Glass orbs
  React.useEffect(() => {
    document.body.classList.toggle('sh-dark', mode === 'dark');
    document.body.classList.toggle('sh-light', mode === 'light');
  }, [mode]);

  const theme = useMemo(
    () =>
      createShessaiTheme({
        mode,
        primaryColorLight,
        primaryColorDark,
        appLogoLight,
        appLogoDark,
        appLogoFavicon,
        appName,
        locale,
        defaultLogoLight,
        defaultLogoDark,
      }),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight,
    ]
  );

  // Ver TAREA: seguir al sistema operativo mientras el usuario no haya
  // elegido manualmente -- este useEffect escribía "preferredTheme" en
  // localStorage en CADA cambio de mode, incluyendo el montaje inicial
  // (que ya venía de prefersDarkMode si no había preferencia previa),
  // persistiendo como "elección del usuario" un valor que a veces solo
  // era heredado del sistema. Eso hacía imposible distinguir ambos casos,
  // y por lo tanto imposible que el seguimiento automático se activara.
  // La escritura ahora vive ÚNICAMENTE en toggleColorMode (arriba, línea
  // ~51) -- ya existía ahí, esta era una escritura duplicada.
  //
  // Este efecto reemplaza esa escritura: revisa localStorage FRESCO cada
  // vez que prefersDarkMode cambia (no una bandera capturada al montar),
  // así que un toggle manual hecho después de montar (que sí escribe
  // localStorage) desactiva el seguimiento automático de inmediato.
  useEffect(() => {
    if (window.localStorage.getItem("preferredTheme")) return;
    setMode(prefersDarkMode ? "dark" : "light");
  }, [prefersDarkMode]);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#0399FF");
      })
      .catch((error) => {
        
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#0399FF");
      })
      .catch((error) => {
        
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        const isValid = file && typeof file === "string" && file.trim().length > 0
          && file !== "null" && file !== "undefined";
        setAppLogoFavicon(
          isValid ? getBackendUrl() + "/public/" + file : defaultLogoFavicon
        );
      })
      .catch((error) => {
        
        setAppLogoFavicon(defaultLogoFavicon);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Shessai");
      })
      .catch((error) => {
        
        setAppName("Shessai");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
